/**
 * OnboardingModal Component - First-time user tutorial
 * Shows a step-by-step guide for new users to understand the app
 */

import React, { useState, useEffect, useContext } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  MobileStepper,
  Fade
} from '@mui/material';
import {
  Inventory2 as BoxIcon,
  CameraAlt as CameraIcon,
  Search as SearchIcon,
  LocationOn as LocationIcon,
  Close as CloseIcon,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  CheckCircle as CheckIcon,
  LocalOffer as TagIcon,
  QrCode2 as QrCodeIcon,
  Lightbulb as TipsIcon,
  SmartToy as AiIcon
} from '@mui/icons-material';
import { colors } from '../styles/styleConstants';
import { AuthContext } from '../context/AuthContext';
import api from '../services/api';

const ONBOARDING_KEY = 'clutterlogix_onboarding_completed';

// Sync onboarding status to backend (for cross-device persistence)
const syncOnboardingToBackend = async (completed) => {
  try {
    await api.put('/users/me/onboarding', { onboarding_completed: completed });
    console.log('[Onboarding] Synced to backend:', completed);
  } catch (error) {
    console.warn('[Onboarding] Failed to sync to backend:', error);
    // Continue anyway - localStorage is the fallback
  }
};

const steps = [
  {
    icon: BoxIcon,
    title: 'Welcome to ClutterLogix!',
    subtitle: 'Your smart inventory assistant',
    description: 'Organize your belongings effortlessly. Take photos, let AI describe them, and find anything in seconds.',
    color: colors.actionBlue,
    tip: 'Perfect for moving, storage, or just knowing where things are!'
  },
  {
    icon: BoxIcon,
    title: 'Step 1: Create a Box',
    subtitle: 'A "box" can be anything!',
    description: 'A box represents any container: a moving box, closet, garage, drawer, or room. Use auto-generated names like "BOX-1" for quick setup, or custom names like "Kitchen Drawer".',
    color: colors.boxesColor,
    tip: 'Tip: Use Locations to organize boxes like folders - Garage → Shelf A → BOX-1'
  },
  {
    icon: CameraIcon,
    title: 'Step 2: Add Items',
    subtitle: 'Snap photos of your stuff',
    description: 'Take photos of items and add them to boxes. Our AI will automatically describe and tag them for you!',
    color: colors.itemsColor,
    tip: 'Tip: You can add multiple items at once with group photos!'
  },
  {
    icon: SearchIcon,
    title: 'Step 3: Find Anything',
    subtitle: 'Search with natural language',
    description: 'Just type what you\'re looking for: "red screwdriver" or "winter clothes". AI search finds items even if you don\'t remember exact names.',
    color: colors.tagsColor,
    tip: 'Tip: Try searching by color, type, or description!'
  },
  {
    icon: TagIcon,
    title: 'Tag Dashboard',
    subtitle: 'Quick visual search',
    description: 'Browse all your items by AI-generated tags. Tap any tag like "Electronics" or "Tools" to instantly see matching items.',
    color: '#9333ea',
    tip: 'Tip: Tags are created automatically from your photos!'
  },
  {
    icon: QrCodeIcon,
    title: 'QR Code Labels',
    subtitle: 'Scan to see what\'s inside',
    description: 'Print QR labels for your boxes. Scan any label with your phone to instantly see the box contents without opening it!',
    color: '#0891b2',
    tip: 'Tip: Great for moving boxes and storage bins!'
  },
  {
    icon: TipsIcon,
    title: 'Organization Tips',
    subtitle: 'Two ways to organize',
    description: '• Let the app organize: Just toss items in boxes without sorting. AI tags everything for easy searching.\n\n• Classic system: Sort items by category into labeled boxes for physical organization.',
    color: '#f59e0b',
    tip: 'Tip: The "toss and tag" method is faster for most people!'
  },
  {
    icon: AiIcon,
    title: 'AI Assistant',
    subtitle: 'Your personal helper',
    description: 'Have questions? Chat with our AI assistant! Ask about app features, get organization tips, or find recommendations for your inventory.',
    color: '#10b981',
    tip: 'Try asking: "How do I organize my garage?"'
  },
  {
    icon: LocationIcon,
    title: 'You\'re Ready!',
    subtitle: 'Start organizing today',
    description: 'Assign locations to boxes like "Garage Shelf" or "Basement". When you search, you\'ll know exactly where to look!',
    color: colors.success,
    tip: 'Let\'s get started! Create your first box.'
  }
];

const OnboardingModal = ({ forceShow = false, onComplete }) => {
  const { currentUser } = useContext(AuthContext);
  
  // Initialize open state based on localStorage to prevent flash
  const [open, setOpen] = useState(() => {
    const hasCompleted = localStorage.getItem(ONBOARDING_KEY);
    return !hasCompleted || forceShow;
  });
  const [activeStep, setActiveStep] = useState(0);

  // Check backend for onboarding status on mount (for cross-device sync)
  useEffect(() => {
    const checkBackendOnboardingStatus = async () => {
      // Only check if user is logged in and localStorage says not completed
      if (!currentUser) return;
      
      const localCompleted = localStorage.getItem(ONBOARDING_KEY);
      
      // If backend says completed, sync to localStorage and close modal
      if (currentUser.onboarding_completed) {
        if (!localCompleted) {
          localStorage.setItem(ONBOARDING_KEY, 'true');
          console.log('[Onboarding] Synced from backend: completed');
        }
        if (!forceShow) {
          setOpen(false);
        }
      }
    };
    
    checkBackendOnboardingStatus();
  }, [currentUser, forceShow]);

  // Handle forceShow prop changes
  useEffect(() => {
    if (forceShow) {
      setOpen(true);
    }
  }, [forceShow]);

  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      handleComplete();
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleComplete = () => {
    localStorage.setItem(ONBOARDING_KEY, 'true');
    syncOnboardingToBackend(true); // Sync to backend for cross-device persistence
    setOpen(false);
    if (onComplete) {
      onComplete();
    }
  };

  const handleSkip = () => {
    localStorage.setItem(ONBOARDING_KEY, 'true');
    syncOnboardingToBackend(true); // Sync to backend for cross-device persistence
    setOpen(false);
    if (onComplete) {
      onComplete();
    }
  };

  const currentStep = steps[activeStep];
  const StepIcon = currentStep.icon;
  const isLastStep = activeStep === steps.length - 1;

  return (
    <Dialog
      open={open}
      onClose={handleSkip}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          maxHeight: '90vh'
        }
      }}
    >
      {/* Close button */}
      <IconButton
        onClick={handleSkip}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: colors.gray[500],
          zIndex: 1
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent sx={{ p: 0 }}>
        {/* Colored header with icon */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${currentStep.color} 0%, ${currentStep.color}dd 100%)`,
            py: 5,
            px: 3,
            textAlign: 'center',
            transition: 'background 0.3s ease'
          }}
        >
          <Fade in key={activeStep} timeout={300}>
            <Box>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2
                }}
              >
                <StepIcon sx={{ fontSize: 40, color: 'white' }} />
              </Box>
              <Typography
                variant="h5"
                sx={{
                  color: 'white',
                  fontWeight: 700,
                  fontFamily: "'Poppins', sans-serif"
                }}
              >
                {currentStep.title}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  mt: 0.5
                }}
              >
                {currentStep.subtitle}
              </Typography>
            </Box>
          </Fade>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3, pb: 2 }}>
          <Fade in key={`content-${activeStep}`} timeout={300}>
            <Box>
              <Typography
                variant="body1"
                sx={{
                  color: colors.charcoal,
                  textAlign: 'center',
                  lineHeight: 1.7,
                  mb: 2,
                  whiteSpace: 'pre-line'
                }}
              >
                {currentStep.description}
              </Typography>

              {/* Tip box */}
              <Box
                sx={{
                  backgroundColor: colors.gray[100],
                  borderRadius: 2,
                  p: 2,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5
                }}
              >
                {isLastStep ? (
                  <CheckIcon sx={{ color: colors.success, mt: 0.25 }} />
                ) : (
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      backgroundColor: currentStep.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ color: 'white', fontWeight: 600 }}
                    >
                      💡
                    </Typography>
                  </Box>
                )}
                <Typography
                  variant="body2"
                  sx={{ color: colors.gray[700] }}
                >
                  {currentStep.tip}
                </Typography>
              </Box>
            </Box>
          </Fade>
        </Box>

        {/* Navigation */}
        <Box sx={{ px: 3, pb: 3 }}>
          <MobileStepper
            variant="dots"
            steps={steps.length}
            position="static"
            activeStep={activeStep}
            sx={{
              backgroundColor: 'transparent',
              justifyContent: 'center',
              mb: 2,
              '& .MuiMobileStepper-dot': {
                width: 10,
                height: 10,
                mx: 0.5
              },
              '& .MuiMobileStepper-dotActive': {
                backgroundColor: currentStep.color
              }
            }}
            backButton={null}
            nextButton={null}
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={handleBack}
              disabled={activeStep === 0}
              startIcon={<KeyboardArrowLeft />}
              sx={{
                flex: 1,
                borderColor: colors.gray[300],
                color: colors.gray[600],
                '&:hover': {
                  borderColor: colors.gray[400],
                  backgroundColor: colors.gray[50]
                }
              }}
            >
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleNext}
              endIcon={isLastStep ? <CheckIcon /> : <KeyboardArrowRight />}
              sx={{
                flex: 1,
                backgroundColor: currentStep.color,
                '&:hover': {
                  backgroundColor: currentStep.color,
                  filter: 'brightness(0.9)'
                }
              }}
            >
              {isLastStep ? "Let's Go!" : 'Next'}
            </Button>
          </Box>

          {/* Skip link */}
          {!isLastStep && (
            <Button
              variant="text"
              onClick={handleSkip}
              sx={{
                mt: 1,
                color: colors.gray[500],
                textTransform: 'none',
                width: '100%',
                '&:hover': {
                  backgroundColor: 'transparent',
                  color: colors.gray[700]
                }
              }}
            >
              Skip tutorial
            </Button>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

// Export helper to reset onboarding (useful for testing)
export const resetOnboarding = () => {
  localStorage.removeItem(ONBOARDING_KEY);
};

// Export helper to check if onboarding is completed
export const isOnboardingCompleted = () => {
  return localStorage.getItem(ONBOARDING_KEY) === 'true';
};

export default OnboardingModal;
